<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card, card, table"
    />

    <template v-else-if="customer">
      <!-- 戻るボタン・卓作成ボタン -->
      <div class="d-flex justify-space-between mb-4">
        <v-btn
          color="secondary"
          :ripple="false"
          @click="goBack"
        >
          <v-icon left>
            {{ icons.mdiArrowLeft }}
          </v-icon>
          一覧に戻る
        </v-btn>
        <v-btn
          color="primary"
          @click="openCreateTableDialog"
        >
          <v-icon left>
            {{ icons.mdiTablePlus }}
          </v-icon>
          卓を作成
        </v-btn>
      </div>

      <!-- 基本情報 -->
      <CustomerBasicInfo
        :customer="customer"
        @update="handleCustomerUpdate"
        @delete="handleCustomerDelete"
      />

      <!-- 関連キャスト -->
      <CustomerCastSelector
        :customer-id="customerId"
      />

      <!-- 顧客メモ -->
      <CustomerNotes
        :customer-id="customerId"
        class="mt-4"
      />

      <!-- ボトルキープ -->
      <CustomerBottleList
        :customer-id="customerId"
        class="mt-4"
      />

      <!-- 来店パターン -->
      <CustomerVisitPattern
        :customer-id="customerId"
        class="mt-4"
      />

      <!-- ノミネーション集計 -->
      <CustomerNominationStats
        :customer-id="customerId"
        class="mt-4"
      />

      <!-- 来店履歴 -->
      <CustomerVisitHistory
        :visit-history="visitHistory"
        :is-loading="isLoadingVisitHistory"
        :total-count="visitHistoryPagination.totalCount"
        :page="visitHistoryPagination.page"
        :per-page="visitHistoryPagination.perPage"
        class="mt-4"
        @update:pagination="handleVisitHistoryPaginationUpdate"
      />

      <!-- 購買履歴 -->
      <CustomerPurchaseHistory
        :purchase-history="purchaseHistory"
        :purchase-items="purchaseItems"
        :is-loading="isLoadingPurchaseHistory"
        :total-count="purchaseHistoryTotalCount"
        :page="purchaseHistoryPage"
        :per-page="purchaseHistoryPerPage"
        class="mt-4"
        @update:pagination="handlePurchaseHistoryPaginationUpdate"
      />

      <!-- 顧客からの卓作成ダイアログ -->
      <CreateTableFromCustomerDialog
        v-model="createTableDialog"
        :customer="customer"
        @created="handleTableCreated"
      />
    </template>

    <div
      v-else
      class="text-center py-8"
    >
      <div class="text-h6 mb-4">
        顧客が見つかりません
      </div>
      <v-btn
        color="primary"
        @click="goBack"
      >
        一覧に戻る
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiArrowLeft, mdiTablePlus } from '@mdi/js'
import useCompInit from '@/views/composable/useCompInit'
import useCustomers from '@/views/v2-temp/common/customer-management/composables/useCustomers'
import CustomerBasicInfo from '@/views/v2-temp/common/customer-management/components/CustomerBasicInfo.vue'
import CustomerCastSelector from '@/views/v2-temp/common/customer-management/components/CustomerCastSelector.vue'
import CustomerBottleList from '@/views/v2-temp/common/customer-management/components/CustomerBottleList.vue'
import CustomerNominationStats from '@/views/v2-temp/common/customer-management/components/CustomerNominationStats.vue'
import CustomerVisitHistory from '@/views/v2-temp/common/customer-management/components/CustomerVisitHistory.vue'
import CustomerPurchaseHistory from '@/views/v2-temp/common/customer-management/components/CustomerPurchaseHistory.vue'
import CustomerNotes from '@/views/v2-temp/common/customer-management/components/CustomerNotes.vue'
import CustomerVisitPattern from '@/views/v2-temp/common/customer-management/components/CustomerVisitPattern.vue'
import CreateTableFromCustomerDialog from '@/views/v2-temp/common/customer-management/components/CreateTableFromCustomerDialog.vue'
import CustomerTagApi from '@/api/waiter/CustomerTag'

export default {
  name: 'WaiterCustomerManagementShow',
  components: {
    CustomerBasicInfo,
    CustomerCastSelector,
    CustomerBottleList,
    CustomerNominationStats,
    CustomerVisitHistory,
    CustomerPurchaseHistory,
    CustomerNotes,
    CustomerVisitPattern,
    CreateTableFromCustomerDialog,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const { initWith, isLoading } = useCompInit()

    const {
      getCustomer,
      updateCustomer,
      deleteCustomer,
      getVisitHistory,
      getPurchaseHistory,
    } = useCustomers(vm)

    const customer = ref(null)
    const customerTags = ref([])
    const createTableDialog = ref(false)
    const visitHistory = ref([])
    const visitHistoryPagination = ref({
      page: 1, perPage: 10, totalCount: 0, totalPages: 0,
    })
    const purchaseHistory = ref({})
    const purchaseItems = ref([])
    const purchaseHistoryPagination = ref({
      page: 1, perPage: 10, totalCount: 0, totalPages: 0,
    })
    const isLoadingVisitHistory = ref(false)
    const isLoadingPurchaseHistory = ref(false)

    const fetchCustomer = async () => {
      customer.value = await getCustomer(props.customerId)
    }

    const fetchCustomerTags = async () => {
      try {
        const res = await CustomerTagApi.getCustomerTags()
        if (res.data.status === 'success') {
          customerTags.value = res.data.data.data || []
        }
      } catch (error) {
        vm.$toast.error('顧客タグの取得に失敗しました')
      }
    }

    const fetchVisitHistory = async ({ page = 1, perPage = 10 } = {}) => {
      isLoadingVisitHistory.value = true
      try {
        const result = await getVisitHistory(props.customerId, { page, perPage })
        visitHistory.value = result.visitHistory
        visitHistoryPagination.value = result.pagination
      } finally {
        isLoadingVisitHistory.value = false
      }
    }

    const handleVisitHistoryPaginationUpdate = ({ page, perPage }) => {
      fetchVisitHistory({ page, perPage })
    }

    const fetchPurchaseHistory = async ({ page = 1, perPage = 10 } = {}) => {
      isLoadingPurchaseHistory.value = true
      try {
        const result = await getPurchaseHistory(props.customerId, { page, perPage })
        purchaseHistory.value = result
        purchaseItems.value = result.purchaseHistory || []
        if (result.pagination) {
          purchaseHistoryPagination.value = result.pagination
        }
      } finally {
        isLoadingPurchaseHistory.value = false
      }
    }

    const handlePurchaseHistoryPaginationUpdate = ({ page, perPage }) => {
      fetchPurchaseHistory({ page, perPage })
    }

    // 購買履歴ページネーション用 computed（安全なアクセス）
    const purchaseHistoryTotalCount = computed(() => purchaseHistoryPagination.value?.totalCount || 0)
    const purchaseHistoryPage = computed(() => purchaseHistoryPagination.value?.page || 1)
    const purchaseHistoryPerPage = computed(() => purchaseHistoryPagination.value?.perPage || 10)

    const goBack = () => {
      vm.$router.push({
        name: 'waiter-settings-customer-management',
        params: {
          club_slug: vm.$route.params.club_slug,
        },
      })
    }

    const handleCustomerUpdate = async event => {
      const result = await updateCustomer(event)
      if (result) {
        customer.value = result
        if (event.callback) event.callback(true)
      } else if (event.callback) event.callback(false)
    }

    const openCreateTableDialog = () => {
      createTableDialog.value = true
    }

    const handleTableCreated = () => {
      createTableDialog.value = false
      vm.$toast.success('卓を作成しました')
      vm.$router.push({
        name: 'waiter-halls',
        params: { club_slug: vm.$route.params.club_slug },
      })
    }

    const handleCustomerDelete = async event => {
      const success = await deleteCustomer(event)
      if (success) {
        goBack()
      }
      if (event.callback) event.callback()
    }

    onMounted(() => {
      initWith([
        fetchCustomer(),
        fetchCustomerTags(),
      ])
      // 履歴は別途非同期で取得
      fetchVisitHistory()
      fetchPurchaseHistory()
    })

    return {
      isLoading,
      customer,
      customerTags,
      visitHistory,
      visitHistoryPagination,
      purchaseHistory,
      purchaseItems,
      purchaseHistoryTotalCount,
      purchaseHistoryPage,
      purchaseHistoryPerPage,
      isLoadingVisitHistory,
      isLoadingPurchaseHistory,
      createTableDialog,
      handleCustomerUpdate,
      handleCustomerDelete,
      handleVisitHistoryPaginationUpdate,
      handlePurchaseHistoryPaginationUpdate,
      openCreateTableDialog,
      handleTableCreated,
      goBack,
      icons: {
        mdiArrowLeft,
        mdiTablePlus,
      },
    }
  },
}
</script>
